import {omit, omitBy} from "lodash";
import * as Yup from "yup";

import {getOfferListUrl} from "../offer/list/url_utils/get_offer_list_url";

export interface SearchFormValues {
    make: string;
    model: string;
    location: string;
    subLocation: string;
    typ: string;
    fuel: string;
    yearFrom: string;
    yearTo: string;
    price__gte: string;
    price__lte: string;
    millage__gte: string;
    millage__lte: string;
    gearbox: string;
    sort: string;
}

export const emptySearchFormValues: SearchFormValues = {
    make: "",
    model: "",
    location: "",
    subLocation: "",
    typ: "",
    fuel: "",
    yearFrom: "",
    yearTo: "",
    price__gte: "",
    price__lte: "",
    millage__gte: "",
    millage__lte: "",
    gearbox: "",
    sort: ""
};

export const searchFormValidationSchema = Yup.object({
    make: Yup.string(),
    model: Yup.string(),
    location: Yup.string(),
    subLocation: Yup.string(),
    typ: Yup.string(),
    fuel: Yup.string(),
    yearFrom: Yup.string(),
    yearTo: Yup.string(),
    price__gte: Yup.string(),
    price__lte: Yup.string(),
    millage__gte: Yup.string(),
    millage__lte: Yup.string(),
    gearbox: Yup.string(),
    sort: Yup.string()
});

export const PRICE_FILTER_OPTIONS = [
    {value: "10000", label: "10 000 zł"},
    {value: "20000", label: "20 000 zł"},
    {value: "30000", label: "30 000 zł"},
    {value: "40000", label: "40 000 zł"},
    {value: "50000", label: "50 000 zł"},
    {value: "75000", label: "75 000 zł"},
    {value: "100000", label: "100 000 zł"},
    {value: "150000", label: "150 000 zł"},
    {value: "200000", label: "200 000 zł"}
];

export const MILEAGE_FILTER_OPTIONS = [
    {value: "50000", label: "50 000 km"},
    {value: "100000", label: "100 000 km"},
    {value: "150000", label: "150 000 km"},
    {value: "200000", label: "200 000 km"},
    {value: "250000", label: "250 000 km"},
    {value: "300000", label: "300 000 km"}
];

export const GEARBOX_FILTER_OPTIONS = [
    {value: "manualna", label: "Manualna"},
    {value: "automatyczna", label: "Automatyczna"}
];

/** String values match API `sort` + SortType in offer_list_reducer (avoid importing reducer — circular dep). */
export const SORT_FILTER_OPTIONS = [
    {value: "-created", label: "Najnowsze"},
    {value: "price", label: "Cena: od najniższej"},
    {value: "-price", label: "Cena: od najwyższej"}
];

/** Full year list for filters — independent of selected make. */
export const YEAR_FILTER_OPTIONS: string[] = (() => {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let year = currentYear; year >= 1990; year -= 1) {
        years.push(String(year));
    }
    return years;
})();

/** Query keys forwarded to list URL / API (path fields excluded). */
export const SEARCH_QUERY_PARAM_KEYS: Array<keyof SearchFormValues> = [
    "model",
    "typ",
    "fuel",
    "yearFrom",
    "yearTo",
    "price__gte",
    "price__lte",
    "millage__gte",
    "millage__lte",
    "gearbox",
    "sort"
];

export const buildOfferListUrlFromSearchValues = (
    values: SearchFormValues,
    extras?: {location?: string; subLocation?: string; offerType?: string}
) => {
    const cleanedValues = omitBy(values, (value) => value === "") as Partial<SearchFormValues>;
    return getOfferListUrl({
        offerType: extras?.offerType || "osobowe",
        type: values.make || undefined,
        location: values.location || extras?.location || undefined,
        subLocation: values.subLocation || extras?.subLocation || undefined,
        ...omit(cleanedValues, "make", "location", "subLocation")
    });
};

export const searchValuesFromQuery = (
    query: Record<string, string | string[] | undefined>,
    make = "",
    location = "",
    subLocation = ""
): SearchFormValues => {
    const pick = (key: keyof SearchFormValues) => {
        const raw = query[key];
        if (Array.isArray(raw)) {
            return raw[0] || "";
        }
        return raw || "";
    };

    return {
        ...emptySearchFormValues,
        make: make || "",
        model: pick("model"),
        location: location || "",
        subLocation: subLocation || "",
        typ: pick("typ"),
        fuel: pick("fuel"),
        yearFrom: pick("yearFrom"),
        yearTo: pick("yearTo"),
        price__gte: pick("price__gte"),
        price__lte: pick("price__lte"),
        millage__gte: pick("millage__gte"),
        millage__lte: pick("millage__lte"),
        gearbox: pick("gearbox"),
        sort: pick("sort")
    };
};
